import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { UserCircle, Users, House, ShieldWarning, ScanSmiley, Airplane, BookOpen, Sparkle, MosqueIcon, HandHeart, Broom, Shield, FirstAid } from "@phosphor-icons/react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Legend } from "recharts";

const Stat = ({ icon: Icon, label, value, tint = "green", testid }) => (
  <div className="imsyaf-card p-5" data-testid={testid}>
    <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg bg-${tint}-100 text-${tint}-800`}>
      <Icon size={22} weight="duotone" />
    </div>
    <div className="mt-4 font-display text-3xl font-bold text-[#064E3B]">{value ?? "—"}</div>
    <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{label}</div>
  </div>
);

export default function Dashboard() {
  const [sum, setSum] = useState(null);
  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSum(r.data)).catch(() => {});
  }, []);

  const c = sum?.counts || {};
  const trend = sum?.attendance_trend || [];
  const top = sum?.top_pelanggar || [];

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400">Ringkasan</div>
        <h1 className="font-display text-3xl font-bold text-[#064E3B]">Dashboard IMSYAF</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <Stat icon={UserCircle} label="Santri Aktif" value={c.santri} testid="stat-santri" />
        <Stat icon={House} label="Kamar" value={c.kamar} testid="stat-kamar" />
        <Stat icon={BookOpen} label="Halaqah" value={c.halaqah} testid="stat-halaqah" />
        <Stat icon={Users} label="Divisi" value={c.divisi} testid="stat-divisi" />
        <Stat icon={ScanSmiley} label="Absensi Hari Ini" value={c.hadir_today} testid="stat-absen" />
        <Stat icon={Airplane} label="Izin Aktif" value={c.izin_active} testid="stat-izin" />
        <Stat icon={ShieldWarning} label="Pelanggaran (7 hari)" value={c.pelanggaran_week} testid="stat-pel" />
        <Stat icon={Sparkle} label="Setoran Tahfiz (7 hari)" value={c.tahfiz_week} testid="stat-tahfiz" />
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Divisi</div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            ["/divisi/keibadahan", "Keibadahan", MosqueIcon || Sparkle, "emerald"],
            ["/divisi/lugho", "Lugho", HandHeart, "teal"],
            ["/divisi/kedisiplinan", "Kedisiplinan", ShieldWarning, "amber"],
            ["/divisi/kebersihan", "Kebersihan", Broom, "sky"],
            ["/divisi/keamanan", "Keamanan", Shield, "slate"],
            ["/divisi/kesehatan", "Kesehatan", FirstAid, "rose"],
            ["/tahfiz-halaqah", "Tahfiz", BookOpen, "green"],
          ].map(([to, l, Ic]) => (
            <Link key={to} to={to} data-testid={`dashboard-divisi-${l.toLowerCase()}`}
              className="imsyaf-card p-4 flex flex-col items-start gap-2 hover:border-green-700 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-green-50 grid place-items-center text-[#166534]">
                <Ic size={22} weight="duotone" />
              </div>
              <div className="font-display font-semibold text-[#064E3B]">{l}</div>
              <div className="text-[11px] text-slate-500">Absensi & Pelanggaran</div>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="imsyaf-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold text-[#064E3B]">Tren Kehadiran 7 Hari Terakhir</h3>
          </div>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="tanggal" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="total" stroke="#166534" strokeWidth={2} name="Total Absen" dot={false} />
                <Line type="monotone" dataKey="hadir" stroke="#4ade80" strokeWidth={2} name="Hadir" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="imsyaf-card p-5">
          <h3 className="font-display text-lg font-semibold text-[#064E3B]">Poin Pelanggaran Tertinggi</h3>
          {top.length === 0 ? (
            <p className="text-sm text-slate-500 mt-3">Belum ada data pelanggaran.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {top.map((p) => (
                <li key={p.santri_id} className="flex items-center justify-between text-sm">
                  <div className="min-w-0">
                    <div className="font-medium text-[#064E3B] truncate">{p.nama || "—"}</div>
                    <div className="text-xs text-slate-500">{p.nis || "—"} · {p.count}x</div>
                  </div>
                  <span className="imsyaf-badge bg-red-100 text-red-700">{p.poin} poin</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
